import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './AirbnbCalendar.css';
import BookingForm from './BookingForm';
import { useTranslation } from 'react-i18next';
import useIcalUnavailableRanges from '../hooks/useIcalUnavailableRanges';

const localeMap = {
  fr: 'fr-FR',
  it: 'it-IT',
  en: 'en-US',
  de: 'de-DE',
  pt: 'pt-PT',
  es: 'es-ES',
  ru: 'ru-RU'
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const normalizeDate = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const diffDaysUtc = (startDate, endDate) => {
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);
  const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((utcEnd - utcStart) / MS_PER_DAY);
};

const toYmd = (date) => {
  const d = normalizeDate(date);
  return d.toISOString().slice(0, 10);
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const parseYmd = (ymd) => {
  if (typeof ymd !== 'string') {
    return null;
  }
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) {
    return null;
  }
  return new Date(y, m - 1, d, 0, 0, 0, 0);
};

// Construit une Date telle que toYmd(date) === ymd, quel que soit le fuseau
// (compense le décalage UTC de toYmd, cf. clés d'overlay stockées à -1 jour).
const dateForKeyPart = (ymd) => {
  const base = parseYmd(ymd);
  if (!base) {
    return null;
  }
  let d = normalizeDate(base);
  if (toYmd(d) !== ymd) {
    d = normalizeDate(new Date(base.getTime() + MS_PER_DAY));
    if (toYmd(d) !== ymd) {
      d = normalizeDate(new Date(base.getTime() - MS_PER_DAY));
    }
  }
  return d;
};

const SOURCE_LABELS = { airbnb: 'Airbnb', booking: 'Booking', vrbo: 'Vrbo' };

const overlayHasContent = (overlay) =>
  Boolean(
    overlay &&
      typeof overlay === 'object' &&
      ((typeof overlay.name === 'string' && overlay.name.trim()) ||
        Number(overlay.totalNet) > 0 ||
        Number(overlay.totalGross) > 0 ||
        (Array.isArray(overlay.comments) && overlay.comments.length > 0)),
  );

const overlayKeyCandidatesForRange = (range) => {
  if (!range?.start || !range?.end) {
    return [];
  }

  const base = `${toYmd(range.start)}_${toYmd(range.end)}`;
  // Tolère un décalage classique (+1 jour) entre iCal et clés overlay.
  const shifted = `${toYmd(addDays(range.start, 1))}_${toYmd(addDays(range.end, 1))}`;
  return [base, shifted];
};

const getOverlayForRange = (overlayMap, range) => {
  if (!overlayMap || typeof overlayMap !== 'object') {
    return null;
  }

  const keys = overlayKeyCandidatesForRange(range);
  for (const key of keys) {
    const overlay = overlayMap[key];
    if (overlay && typeof overlay === 'object') {
      return overlay;
    }
  }

  return null;
};


const AirbnbCalendar = ({
  lang = 'fr',
  readOnly = false,
  showPrices = true,
  alternateReservedPeriodColors = false,
  withSources,
  showSourceBadges,
  unavailableRanges: unavailableRangesOverride,
  showOwnerStayBadge = true,
}) => {
  const { t } = useTranslation();

  const calendarLocale = localeMap[lang] || 'en-US';

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('villaAuthenticated') === 'true';
  });
  const [selectedRange, setSelectedRange] = useState(null);
  const [rangeStart, setRangeStart] = useState(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [tarifs, setTarifs] = useState([]);
  const [today] = useState(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  });
  const [error, setError] = useState('');
  const [modalReservation, setModalReservation] = useState(null);

  const effectiveWithSources = typeof withSources === 'boolean' ? withSources : isAuthenticated;
  const effectiveShowSourceBadges = typeof showSourceBadges === 'boolean' ? showSourceBadges : isAuthenticated;
  const shouldFetchRanges = !Array.isArray(unavailableRangesOverride);

  const { ranges: fetchedRanges } = useIcalUnavailableRanges({
    withSources: effectiveWithSources,
    enabled: shouldFetchRanges,
  });

  const unavailableRangesRaw = Array.isArray(unavailableRangesOverride)
    ? unavailableRangesOverride
    : fetchedRanges;

  const [overlays, setOverlays] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    const loadOverlays = async () => {
      try {
        const response = await fetch('/api/bookings/overlays.php', { cache: 'no-store' });
        const payload = await response.json();
        const loaded = payload?.success && payload?.data?.overlays ? payload.data.overlays : {};
        if (!isCancelled) {
          setOverlays(loaded);
        }
      } catch (_) {
        if (!isCancelled) {
          setOverlays({});
        }
      }
    };

    loadOverlays();

    return () => {
      isCancelled = true;
    };
  }, []);

  // Réservations passées (issues des overlays) à réafficher sur le calendrier —
  // uniquement pour le propriétaire connecté, et seulement si le calendrier récupère
  // lui-même ses plages (calendrier public du site, pas les vues admin en lecture seule).
  const pastOverlayRanges = useMemo(() => {
    if (!shouldFetchRanges || !isAuthenticated || !overlays || typeof overlays !== 'object') {
      return [];
    }
    const today = normalizeDate(new Date());
    const feedKeys = new Set();
    for (const r of Array.isArray(unavailableRangesRaw) ? unavailableRangesRaw : []) {
      if (r?.start && r?.end) {
        feedKeys.add(`${toYmd(r.start)}_${toYmd(r.end)}`);
      }
    }

    const rangesOverlap = (a, b) =>
      normalizeDate(a.start) < normalizeDate(b.end) && normalizeDate(b.start) < normalizeDate(a.end);

    const entries = Object.entries(overlays)
      .filter(([, ov]) => overlayHasContent(ov) && ov.bookingMode !== 'errore')
      .map(([key, ov]) => {
        const [a, b] = key.split('_');
        return { key, ov, start: dateForKeyPart(a), end: dateForKeyPart(b) };
      })
      .filter((e) => e.start && e.end && normalizeDate(e.end) <= today && !feedKeys.has(e.key))
      .sort((x, y) => diffDaysUtc(x.start, x.end) - diffDaysUtc(y.start, y.end));

    const added = [];
    const result = [];
    for (const e of entries) {
      if (added.some((r) => rangesOverlap(r, e))) {
        continue;
      }
      added.push({ start: e.start, end: e.end });
      const sourceId = ['airbnb', 'booking', 'vrbo'].includes(e.ov.bookingMode) ? e.ov.bookingMode : null;
      result.push({
        start: e.start,
        end: e.end,
        sourceId,
        sourceLabel: sourceId ? SOURCE_LABELS[sourceId] : null,
        isPastOverlay: true,
      });
    }
    return result;
  }, [shouldFetchRanges, isAuthenticated, overlays, unavailableRangesRaw]);

  const unavailableRanges = useMemo(() => {
    const base = Array.isArray(unavailableRangesRaw) ? unavailableRangesRaw : [];
    const list = [...base, ...pastOverlayRanges];
    const overlayMap = overlays && typeof overlays === 'object' ? overlays : null;
    if (!overlayMap) {
      return list;
    }

    return list.filter((range) => {
      const keys = overlayKeyCandidatesForRange(range);
      for (const key of keys) {
        const overlay = overlayMap[key];
        if (overlay && overlay.bookingMode === 'errore') {
          return false;
        }
      }
      return true;
    });
  }, [overlays, unavailableRangesRaw, pastOverlayRanges]);

  const sortedReservationRanges = useMemo(() => {
    return (unavailableRanges || [])
      .filter((range) => range?.start && range?.end)
      .map((range) => ({
        ...range,
        start: normalizeDate(range.start),
        end: normalizeDate(range.end),
      }))
      .sort((left, right) => left.start - right.start);
  }, [unavailableRanges]);

  const getReservationIndexForTile = (date) => {
    const day = normalizeDate(date);
    const time = day.getTime();

    for (let index = 0; index < sortedReservationRanges.length; index += 1) {
      const range = sortedReservationRanges[index];

      // Jours de réservation: [start, end)
      if (day >= range.start && day < range.end) {
        return index;
      }

      // Jour de sortie (end) affiché comme diagonale: on l'associe au même créneau.
      if (time === range.end.getTime()) {
        return index;
      }
    }

    return null;
  };

  const isSameDay = (left, right) => {
    if (!left || !right) {
      return false;
    }
    return normalizeDate(left).getTime() === normalizeDate(right).getTime();
  };

  useEffect(() => {
    const syncAuthentication = () => {
      setIsAuthenticated(localStorage.getItem('villaAuthenticated') === 'true');
    };

    window.addEventListener('storage', syncAuthentication);
    window.addEventListener('villa-auth-changed', syncAuthentication);

    return () => {
      window.removeEventListener('storage', syncAuthentication);
      window.removeEventListener('villa-auth-changed', syncAuthentication);
    };
  }, []);

  useEffect(() => {
    if (readOnly && !showPrices) {
      setTarifs([]);
      return;
    }

    const fetchTarifs = async () => {
      try {
        const res = await fetch(`/json/tarifs.json?v=${Date.now()}`);
        const data = await res.json();
        const cleaned = data.map(item => ({
          start: parseDate(item['Date de début']),
          end: parseDate(item['Date de fin']),
          prix: Number(item['Prix par nuit']),
          minNuits: Number(item['Nuits minimum'])
        }));
        setTarifs(cleaned);
      } catch (err) {
        console.error('Erreur lors du chargement des tarifs :', err);
      }
    };

    fetchTarifs();
  }, [readOnly, showPrices]);

  useEffect(() => {
    if (!isBookingModalOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsBookingModalOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isBookingModalOpen]);

  const parseDate = (str) => {
    const [day, month, year] = str.split('/');
    return new Date(`${year}-${month}-${day}`);
  };

  const isReserved = (date) => {
    const day = normalizeDate(date);
    return (unavailableRanges || []).some((range) => day >= range.start && day < range.end);
  };

  const getReservationForDate = (date) => {
    const day = normalizeDate(date);
    return (unavailableRanges || []).find((range) => day >= range.start && day < range.end) || null;
  };

  const isCheckoutBoundary = (date) => {
    return (unavailableRanges || []).some((range) => isSameDay(date, range.start));
  };

  const isSlotStart = (date) => {
    return (unavailableRanges || []).some((range) => isSameDay(date, range.start));
  };

  // Jour de checkout du créneau (fin exclusive du range iCal).
  // Ex: réservation 18 -> 25 : on veut la diagonale de sortie sur le 25.
  const isSlotEnd = (date) => {
    const day = normalizeDate(date);
    return (unavailableRanges || []).some((range) => day.getTime() === normalizeDate(range.end).getTime());
  };

  const getTarifForDate = useCallback(
    (date) => {
      return tarifs.find((t) => date >= t.start && date <= t.end);
    },
    [tarifs]
  );

  const windowEndExclusive = useMemo(() => {
    const end = new Date(today.getFullYear(), today.getMonth() + 12, today.getDate());
    end.setHours(0, 0, 0, 0);
    return end;
  }, [today]);

  const availablePeriods = useMemo(() => {
    const reservedRanges = unavailableRanges
      .map((range) => ({
        start: normalizeDate(range.start),
        end: normalizeDate(range.end),
      }))
      .filter((range) => range.end > today && range.start < windowEndExclusive)
      .sort((a, b) => a.start - b.start)
      .reduce((acc, current) => {
        if (acc.length === 0) {
          acc.push({ ...current });
          return acc;
        }

        const last = acc[acc.length - 1];
        if (current.start <= last.end) {
          if (current.end > last.end) {
            last.end = current.end;
          }
        } else {
          acc.push({ ...current });
        }

        return acc;
      }, []);

    const periods = [];
    let cursor = today;

    reservedRanges.forEach((range) => {
      const boundedStart = range.start > windowEndExclusive ? windowEndExclusive : range.start;
      const boundedEnd = range.end > windowEndExclusive ? windowEndExclusive : range.end;

      if (boundedStart > cursor) {
        periods.push({
          start: new Date(cursor),
          end: new Date(boundedStart),
        });
      }

      if (boundedEnd > cursor) {
        cursor = new Date(boundedEnd);
      }
    });

    if (cursor < windowEndExclusive) {
      periods.push({
        start: new Date(cursor),
        end: new Date(windowEndExclusive),
      });
    }

    return periods.map((period) => {
      const endDisplay = new Date(period.end);
      return {
        ...period,
        endDisplay,
        nights: diffDaysUtc(period.start, period.end),
      };
    });
  }, [unavailableRanges, today, windowEndExclusive]);

  const shortStaySlots = useMemo(() => {
    if (!tarifs.length) {
      return [];
    }

    return availablePeriods
      .map((period) => {
        const applicableTarif = getTarifForDate(period.start);
        const minNuits = applicableTarif?.minNuits || 1;
        const nights = period.nights;

        if (nights > 1 && nights < minNuits) {
          return {
            ...period,
            minNuits,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [availablePeriods, tarifs, getTarifForDate]);

  const isShortStayDay = (date) => {
    const day = normalizeDate(date);
    return shortStaySlots.some((slot) => day >= slot.start && day < slot.end);
  };

  const lostNightSlots = useMemo(() => {
    return availablePeriods.filter((period) => period.nights === 1);
  }, [availablePeriods]);

  const isLostNightDay = (date) => {
    const day = normalizeDate(date);
    return lostNightSlots.some((slot) => day >= slot.start && day < slot.end);
  };

  const isPast = (date) => normalizeDate(date) < today;
  const isUnavailable = (date) => {
    if (isPast(date)) {
      return true;
    }

    // Une seule nuit entre deux réservations: on la bloque (nuit perdue).
    if (isLostNightDay(date)) {
      return true;
    }

    if (!isReserved(date)) {
      return false;
    }

    // Un jour d'entrée (début de réservation) doit rester sélectionnable comme checkout.
    if (isCheckoutBoundary(date)) {
      return false;
    }

    // Si l'utilisateur a déjà cliqué une date de début et choisit la date de fin,
    // on autorise la date de fin sur un "jour d'entrée" (début de réservation existante).
    const isPickingEndDate = rangeStart instanceof Date;
    if (isPickingEndDate) {
      return !isCheckoutBoundary(date);
    }

    return true;
  };

  const getBookingSummary = (range) => {
    if (!Array.isArray(range)) {
      return null;
    }

    const [start, end] = range;
    const adjustedEnd = new Date(end);
    adjustedEnd.setDate(adjustedEnd.getDate() - 1);

    const days = [];
    let current = new Date(start);

    while (current <= adjustedEnd) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    const prixParNuitDetails = days.map(day => {
      const tarif = getTarifForDate(day);
      const base = tarif ? tarif.prix : null;
      if (base == null) {
        return `${day.toLocaleDateString()} : N/A€`;
      }

      if (isShortStayDay(day)) {
        const discounted = Math.round(base * 0.9);
        return `${day.toLocaleDateString()} : ${discounted}€ (-10%)`;
      }

      return `${day.toLocaleDateString()} : ${base}€`;
    });

    const prixTotal = days.reduce((total, day) => {
      const tarif = getTarifForDate(day);
      if (!tarif) {
        return total;
      }

      const base = tarif.prix;
      const price = isShortStayDay(day) ? Math.round(base * 0.9) : base;
      return total + price;
    }, 0);

    return { prixTotal, prixParNuitDetails };
  };

  const handleDateChange = (range) => {
    if (readOnly) {
      return;
    }

    if (!Array.isArray(range)) {
      const start = normalizeDate(range);

      // On interdit un jour réservé comme date d'entrée.
      if (isReserved(start) || isLostNightDay(start)) {
        setSelectedRange(null);
        setRangeStart(null);
        setIsBookingModalOpen(false);
        setError(t('calendar.errorReserved'));
        return;
      }

      setSelectedRange(start);
      setRangeStart(start);
      setIsBookingModalOpen(false);
      return;
    }

    const start = normalizeDate(range[0]);
    const end = normalizeDate(range[1]);
    const diffDays = diffDaysUtc(start, end);
    // console.log(`Selected range: ${start} to ${end}, days: ${diffDays}`);

    let current = new Date(start);
    let containsReserved = false;

    while (current < end) {
      if (isReserved(current) || isLostNightDay(current)) {
        containsReserved = true;
        break;
      }
      current.setDate(current.getDate() + 1);
    }

    const applicableTarif = getTarifForDate(start);
    const minNuits = applicableTarif?.minNuits || 1;

    const containingSlot = availablePeriods.find(
      (period) => start >= period.start && end <= period.end
    );
    const slotNights = containingSlot ? diffDaysUtc(containingSlot.start, containingSlot.end) : 0;
    const canOverrideMinNights =
      !!containingSlot &&
      slotNights > 1 &&
      slotNights < minNuits &&
      diffDays > 1 &&
      diffDays <= slotNights;

    if (containsReserved) {
      setSelectedRange(null);
      setRangeStart(null);
      setIsBookingModalOpen(false);
      setError(t('calendar.errorReserved'));
    } else if (diffDays < minNuits && !canOverrideMinNights) {
      setSelectedRange(null);
      setRangeStart(null);
      setIsBookingModalOpen(false);
      setError(t('calendar.errorMinNights', { count: minNuits }));
    } else {
      setSelectedRange([start, end]);
      setRangeStart(null);
      setIsBookingModalOpen(true);
      setError('');

      // Log la demande de prix ici
      const bookingSummary = getBookingSummary(range);
      const prixTotal = bookingSummary ? bookingSummary.prixTotal : 0;
      const prixParNuitDetails = bookingSummary ? bookingSummary.prixParNuitDetails : [];

      logPriceRequest({
        type: 'price_request',
        startDate: start,
        endDate: end,
        total: prixTotal,
        details: prixParNuitDetails,
        explication: `Demande sur Pina e Paolo du ${start.toLocaleDateString()} au ${end.toLocaleDateString()} (${diffDays} nuits).
        prix total (hors menage) : ${prixTotal}€.
        Détail : ${prixParNuitDetails.join(', ')}.`
      });
    }
  };

  const logPriceRequest = async (payload) => {
    console.log('[logPriceRequest] Payload envoyé :', payload);
    try {
      //console.log('[logPriceRequest] Début requête fetch vers n8n...');
      const response = await fetch('https://n8n.atoneo.com/webhook/dc995df4-1637-4515-bb09-248557253654', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      // console.log('[logPriceRequest] Requête envoyée, attente de la réponse...');
      await response.text();
      // console.log('[logPriceRequest] Réponse brute :', result);
      if (response.ok) {
        // console.log('[logPriceRequest] Succès HTTP');
      } else {
        console.error('[logPriceRequest] Erreur HTTP', response.status, response.statusText);
      }
    } catch (err) {
      console.error('[logPriceRequest] Exception JS :', err);
    }
  };

  const bookingSummary = getBookingSummary(selectedRange);

  // Clic sur un tag de réservation → ouvre la modale d'infos client (sans bloquer le clic
  // de sélection de date : on stoppe la propagation vers la tuile).
  const openReservationModal = (event, reservation) => {
    event.stopPropagation();
    event.preventDefault();
    if (!reservation) {
      return;
    }
    const overlay = getOverlayForRange(overlays, reservation);
    setModalReservation({ range: reservation, overlay });
  };

  const modalNights = modalReservation
    ? diffDaysUtc(modalReservation.range.start, modalReservation.range.end)
    : 0;
  const modalBeds = (() => {
    const beds = modalReservation?.overlay?.beds;
    if (!beds || typeof beds !== 'object') {
      return [];
    }
    const labels = {
      matrimoniale: 'Matrimoniale',
      lettino1: 'Lettino 1',
      lettino2: 'Lettino 2',
      divanoLetto: 'Divano letto',
      lettiniAssemblati: 'Lettini assemblati',
    };
    return Object.entries(labels)
      .filter(([k]) => beds[k])
      .map(([, label]) => label);
  })();
  const modalComments = Array.isArray(modalReservation?.overlay?.comments)
    ? modalReservation.overlay.comments
    : [];

  return (
    <div className="airbnb-calendar-container">
      <h3 className="calendar-title">{t('calendar.title')}</h3>
      <Calendar
        locale={calendarLocale}
        selectRange={!readOnly}
        onChange={readOnly ? undefined : handleDateChange}
        value={readOnly ? null : selectedRange}
        tileDisabled={({ date, view }) =>
          view === 'month' && isUnavailable(date)
        }
        tileContent={({ date, view }) => {
          if (view !== 'month') {
            return null;
          }

          const reservation = getReservationForDate(date);
          const overlayForReservation = reservation ? getOverlayForRange(overlays, reservation) : null;
          const isOwnerStay = Boolean(
            showOwnerStayBadge &&
              isAuthenticated &&
              overlayForReservation &&
              overlayForReservation.bookingMode === 'sejour_proprietaire'
          );

          if (isOwnerStay) {
            return (
              <div className="tile-extra">
                <span className="reservation-badge reservation-badge--owner">Sogg. proprio</span>
                {effectiveShowSourceBadges && reservation?.sourceLabel ? (
                  <>
                    {' '}
                    <span
                      className={
                        (reservation.sourceId
                          ? `reservation-badge reservation-badge--${reservation.sourceId}`
                          : 'reservation-badge') + ' reservation-badge--clickable'
                      }
                      role="button"
                      tabIndex={0}
                      title="Vedi informazioni cliente"
                      onClick={(e) => openReservationModal(e, reservation)}
                    >
                      {reservation.sourceLabel}
                    </span>
                  </>
                ) : null}
              </div>
            );
          }

          if (effectiveShowSourceBadges && reservation?.sourceLabel) {
            return (
              <div className="tile-extra">
                <span
                  className={
                    (reservation.sourceId
                      ? `reservation-badge reservation-badge--${reservation.sourceId}`
                      : 'reservation-badge') + ' reservation-badge--clickable'
                  }
                  role="button"
                  tabIndex={0}
                  title="Vedi informazioni cliente"
                  onClick={(e) => openReservationModal(e, reservation)}
                >
                  {reservation.sourceLabel}
                </span>
              </div>
            );
          }

          if (isAuthenticated && isLostNightDay(date)) {
            return (
              <div className="tile-extra">
                <span className="reservation-badge reservation-badge--lostnight">Perdu</span>
              </div>
            );
          }

          if (!showPrices) {
            if (readOnly) {
              const showStart = isSlotStart(date);
              const showEnd = isSlotEnd(date);

              if (showStart || showEnd) {
                return (
                  <div className="tile-extra">
                    {showStart && <span className="reservation-badge reservation-badge--checkin">Entrata</span>}
                    {showEnd && (
                      <>
                        {showStart ? ' ' : null}
                        <span className="reservation-badge reservation-badge--checkout">Uscita</span>
                      </>
                    )}
                  </div>
                );
              }
            }

            return <div className="tile-extra" />;
          }

          if (!isUnavailable(date) && isShortStayDay(date)) {
            const tarif = getTarifForDate(date);
            const discounted = tarif ? Math.round(tarif.prix * 0.9) : null;

            if (isAuthenticated) {
              return (
                <div className="tile-extra">
                  <span className="reservation-badge reservation-badge--shortstay">Opti</span>
                  {discounted != null && (
                    <div className="price">
                      {t('calendar.pricePerNight', { price: discounted })}{' '}
                      <span className="reservation-badge reservation-badge--promo">-10%</span>
                    </div>
                  )}
                </div>
              );
            }
          }

          if (!isUnavailable(date)) {
            const tarif = getTarifForDate(date);
            if (tarif) {
              if (isShortStayDay(date)) {
                const discounted = Math.round(tarif.prix * 0.9);
                return (
                  <div className="tile-extra price">
                    {t('calendar.pricePerNight', { price: discounted })}{' '}
                    <span className="reservation-badge reservation-badge--promo">-10%</span>
                  </div>
                );
              }

              return (
                <div className="tile-extra price">
                  {t('calendar.pricePerNight', { price: tarif.prix })}
                </div>
              );
            }
          }

          // Spacer pour garder une hauteur identique sur toutes les cases.
          return <div className="tile-extra" />;
        }}
        tileClassName={({ date, view }) => {
          const classes = [];

          if (view === 'month') {
            if (alternateReservedPeriodColors) {
              const reservationIndex = getReservationIndexForTile(date);
              if (typeof reservationIndex === 'number') {
                classes.push(reservationIndex % 2 === 0 ? 'reserved-period-even' : 'reserved-period-odd');
              }
            }

            // Indicateurs visuels de début/fin de créneau.
            if (isSlotStart(date)) {
              classes.push('reserved-slot-start');
            }
            if (isSlotEnd(date)) {
              classes.push('reserved-slot-end');
            }

            // Nuits perdues: même visuel qu'un jour réservé.
            if (isLostNightDay(date)) {
              classes.push('reserved-date');
            }

            // Style "réservé" uniquement pour les jours réellement indisponibles.
            if (isReserved(date)) {
              if (isCheckoutBoundary(date)) {
                classes.push('reserved-boundary');
              } else {
                classes.push('reserved-date');
              }
            }
          }

          // Ajouter les classes pour les jours de début et fin de sélection
          if (selectedRange && Array.isArray(selectedRange)) {
            const [start, end] = selectedRange;
            const dateStr = date.toDateString();
            const startStr = start.toDateString();
            const endStr = end.toDateString();

            if (dateStr === startStr) {
              classes.push('selection-start');
            }
            if (dateStr === endStr) {
              classes.push('selection-end');
            }
          }

          return classes.length > 0 ? classes.join(' ') : null;
        }}
      />


      {!readOnly && error && (
        <p className="calendar-error">
          {error}
        </p>
      )}

      {!readOnly && isBookingModalOpen && selectedRange && Array.isArray(selectedRange) && bookingSummary && (
        <div className="booking-modal-backdrop" onClick={() => setIsBookingModalOpen(false)}>
          <div className="booking-modal" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="booking-modal-close"
              onClick={() => setIsBookingModalOpen(false)}
              aria-label="Fermer la modale de réservation"
            >
              ×
            </button>
            <BookingForm
              selectedRange={selectedRange}
              totalPrice={bookingSummary.prixTotal}
              prixParNuitDetails={bookingSummary.prixParNuitDetails}
            />
          </div>
        </div>
      )}

      {modalReservation && (
        <div className="booking-modal-backdrop" onClick={() => setModalReservation(null)}>
          <div className="client-info-modal" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="booking-modal-close"
              onClick={() => setModalReservation(null)}
              aria-label="Chiudi"
            >
              ×
            </button>

            <h3 className="client-info-title">
              {modalReservation.overlay?.name?.trim() || 'Prenotazione'}
            </h3>

            <div className="client-info-dates">
              {modalReservation.range.start.toLocaleDateString(calendarLocale)}
              {' → '}
              {modalReservation.range.end.toLocaleDateString(calendarLocale)}
              <span className="client-info-nights">{modalNights} notti</span>
            </div>

            <dl className="client-info-grid">
              {modalReservation.range.sourceLabel ? (
                <>
                  <dt>Piattaforma</dt>
                  <dd>{modalReservation.range.sourceLabel}</dd>
                </>
              ) : null}
              {(modalReservation.overlay?.adults || modalReservation.overlay?.children) ? (
                <>
                  <dt>Ospiti</dt>
                  <dd>
                    {modalReservation.overlay?.adults || 0} adulti, {modalReservation.overlay?.children || 0} bambini
                  </dd>
                </>
              ) : null}
              {modalReservation.overlay?.country ? (
                <>
                  <dt>Paese</dt>
                  <dd>{modalReservation.overlay.country}</dd>
                </>
              ) : null}
              {modalBeds.length ? (
                <>
                  <dt>Letti</dt>
                  <dd>{modalBeds.join(', ')}</dd>
                </>
              ) : null}
              {modalReservation.overlay?.notes?.trim() ? (
                <>
                  <dt>Note</dt>
                  <dd>{modalReservation.overlay.notes}</dd>
                </>
              ) : null}
            </dl>

            {modalComments.length ? (
              <div className="client-info-comments">
                <div className="client-info-comments-title">Commenti</div>
                <ul>
                  {modalComments.map((c, i) => (
                    <li key={`${c.at || i}-${i}`}>
                      <span className="client-info-comment-text">{c.text}</span>
                      <span className="client-info-comment-meta">
                        {c.by ? `${c.by} · ` : ''}
                        {c.at ? new Date(c.at).toLocaleDateString(calendarLocale) : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {!modalReservation.overlay && (
              <p className="client-info-empty">Nessun dato cliente registrato per questa prenotazione.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AirbnbCalendar;
